import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table, Tag, Button, Input, Select, DatePicker, Row, Col, Tooltip, Empty, Space,
} from 'antd';
import { PlusOutlined, ReloadOutlined, FileTextOutlined, LinkOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Layout from '../../components/Layout';
import TableExportButton from '../../components/TableExportButton';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate } from '../../variables/constants';
import NouveauPvModal from './NouveauPvModal';
import DetailPvModal from './DetailPvModal';
import { NumeroPvAffiche } from './numeroPv';
import PageHeader from '../../components/PageHeader';
import KpiCards from '../../components/KpiCards';

import { useTranslation } from 'react-i18next';

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const GREEN = '#1a6b3c';

const FlagTag = ({ value, label }) => (
  <Tag
    color={value === 'OK' ? 'green' : 'default'}
    style={{ borderRadius: 20, fontSize: 11, margin: 0 }}
  >
    {value === 'OK' ? '✓' : '-'} {label}
  </Tag>
);

export default function ListPvSinistres() {
  const { t } = useTranslation('pvSinistres');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const modeNonAssocies = location.pathname.endsWith('/non-associes');

  // Si on arrive depuis ApercuSinistre avec ?sinistreId=..., on ouvre le modal directement
  const sinistreIdParam = searchParams.get('sinistreId');

  const [pvs, setPvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [filtreCompletude, setFiltreCompletude] = useState('');
  const [dateRange, setDateRange] = useState(null); // [dayjs, dayjs] | null
  const [createOpen, setCreateOpen] = useState(!!sinistreIdParam);
  const [detailPvId, setDetailPvId] = useState(null);
  const [stats, setStats] = useState(null);

  const load = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      if (modeNonAssocies) {
        const res = await axiosInstance.get('/v1/pv/non-associes');
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setPvs(list);
        setPagination(prev => ({ ...prev, current: 1, total: list.length }));
      } else {
        // La complétude est filtrée PAR LE SERVEUR (paramètre `estComplet`, déjà
        // supporté par GET /v1/pv). Le filtrer côté client ne triait que les vingt
        // lignes de la page affichée : demander « les incomplets » rendait une
        // poignée de résultats et laissait croire qu'il n'y en avait pas d'autres.
        const params = new URLSearchParams({ page, size: 20 });
        if (filtreCompletude) params.set('estComplet', filtreCompletude);
        const res = await axiosInstance.get(`/v1/pv?${params}`);
        setPvs(Array.isArray(res.data?.data) ? res.data.data : []);
        setPagination(prev => ({ ...prev, current: page + 1, total: res.data?.totalElements || 0 }));
      }
    } catch {
      setPvs([]);
    } finally {
      setLoading(false);
    }
  }, [modeNonAssocies, filtreCompletude]);

  useEffect(() => { load(0); }, [load]);

  // Compteurs de l'en-tête : comptés en base sur TOUT le stock, et donc stables
  // quels que soient la page et le filtre en cours - c'est ce qui en fait des
  // repères. En mode « non-associés », la liste chargée est complète : on compte
  // dessus plutôt que d'afficher les totaux d'un autre périmètre.
  //
  // Rechargés à l'ouverture et après chaque écriture, pas à chaque changement de
  // page : un total qui ne bouge pas n'a aucune raison d'être redemandé vingt fois
  // pendant qu'on feuillette la liste.
  const rafraichirStats = useCallback(() => {
    if (modeNonAssocies) return;
    axiosInstance.get('/v1/pv/statistiques', { skipErrorToast: true })
      .then((r) => setStats(r.data?.data || null))
      .catch(() => setStats(null));
  }, [modeNonAssocies]);

  useEffect(() => { rafraichirStats(); }, [rafraichirStats]);

  // Si sinistreId arrive après le premier rendu (navigation), ouvre le modal
  useEffect(() => {
    if (sinistreIdParam) setCreateOpen(true);
  }, [sinistreIdParam]);

  const filtered = useMemo(() => pvs.filter(p => {
    // Recherche textuelle
    if (search) {
      const q = search.toLowerCase();
      const match =
        p.numeroPv?.toLowerCase().includes(q) ||
        p.lieuAccident?.toLowerCase().includes(q) ||
        p.referenceSinistreLiee?.toLowerCase().includes(q) ||
        p.numeroSinistreLocal?.toLowerCase().includes(q) ||
        p.entiteConstatNom?.toLowerCase().includes(q);
      if (!match) return false;
    }
    // Complétude : filtrée par le serveur en mode paginé (cf. `load`). Ici, elle
    // ne concerne que le mode « non-associés », dont la liste arrive entière.
    if (modeNonAssocies) {
      if (filtreCompletude === 'true' && !p.estComplet) return false;
      if (filtreCompletude === 'false' && p.estComplet) return false;
    }
    // Filtre plage de dates (dateReceptionBncb)
    if (dateRange?.[0] && dateRange?.[1]) {
      const recu = p.dateReceptionBncb ? dayjs(p.dateReceptionBncb) : null;
      if (!recu) return false;
      if (recu.isBefore(dateRange[0], 'day') || recu.isAfter(dateRange[1], 'day')) return false;
    }
    return true;
  }), [pvs, search, filtreCompletude, dateRange, modeNonAssocies]);

  /**
   * Cartes de tête : total, complets, incomplets.
   *
   * Elles servent de filtre : cliquer une carte applique l'état correspondant,
   * recliquer la même l'enlève. Un compteur qu'on lit sans pouvoir l'ouvrir
   * oblige à aller rechercher la même chose dans un menu déroulant deux
   * centimètres plus bas.
   *
   * En mode paginé, les valeurs viennent du serveur ; en mode « non-associés »,
   * la liste chargée est complète, on compte donc dessus.
   */
  const kpis = useMemo(() => {
    const total = modeNonAssocies ? pvs.length : (stats?.total ?? null);
    const complets = modeNonAssocies
      ? pvs.filter((p) => p.estComplet).length
      : (stats?.complets ?? null);
    const incomplets = modeNonAssocies
      ? pvs.filter((p) => !p.estComplet).length
      : (stats?.incomplets ?? null);

    const bascule = (valeur) => () =>
      setFiltreCompletude((actuel) => (actuel === valeur ? '' : valeur));

    return [
      {
        label: modeNonAssocies ? t('liste.nonAssocies') : t('liste.kpi.total'),
        value: total ?? '-',
        color: GREEN,
        onClick: () => setFiltreCompletude(''),
        active: filtreCompletude === '',
      },
      {
        label: t('liste.kpi.complets'),
        value: complets ?? '-',
        color: '#16a34a',
        onClick: bascule('true'),
        active: filtreCompletude === 'true',
      },
      {
        // Le chiffre qui compte au quotidien : ce sont les PV sur lesquels il
        // reste quelque chose à faire.
        label: t('liste.kpi.incomplets'),
        value: incomplets ?? '-',
        color: '#d97706',
        onClick: bascule('false'),
        active: filtreCompletude === 'false',
      },
    ];
  }, [modeNonAssocies, pvs, stats, filtreCompletude, t]);

  const columns = [
    {
      title: t('liste.colonnes.numero'), dataIndex: 'numeroPv', key: 'num', width: 180, fixed: 'left',
      render: (v) => <strong style={{ color: GREEN, fontSize: 13 }}><NumeroPvAffiche numero={v} /></strong>,
    },
    {
      title: t('commun.uniteRedactrice'), dataIndex: 'entiteConstatNom', key: 'entite', width: 180, ellipsis: true,
      render: (v) => v || '-',
    },
    {
      title: t('liste.colonnes.lieu'), dataIndex: 'lieuAccident', key: 'lieu', ellipsis: true,
      render: (v) => <span style={{ fontSize: 13 }}>{v || '-'}</span>,
    },
    {
      title: t('liste.colonnes.accident'), dataIndex: 'dateAccidentPv', key: 'dacc', width: 100, align: 'center',
      format: 'date',
      render: (v) => <span style={{ fontSize: 12, color: '#6b7280' }}>{formatDate(v)}</span>,
    },
    {
      title: t('liste.colonnes.recu'), dataIndex: 'dateReceptionBncb', key: 'drec', width: 100, align: 'center',
      format: 'date',
      render: (v) => <span style={{ fontSize: 12, color: '#6b7280' }}>{formatDate(v)}</span>,
    },
    {
      title: t('liste.colonnes.contenu'), key: 'contenu', width: 280,
      export: (_, r) => {
        if (!r) return '';
        const parts = [];
        if (r.aCirconstances === 'OK') parts.push(t('liste.abreviations.circonstances'));
        if (r.aAuditions === 'OK') parts.push(t('liste.abreviations.auditions'));
        if (r.aCroquis === 'OK') parts.push(t('liste.abreviations.croquis'));
        return parts.length ? parts.join(', ') : '-';
      },
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <FlagTag value={r.aCirconstances} label={t('liste.abreviations.circonstances')} />
          <FlagTag value={r.aAuditions} label={t('liste.abreviations.auditions')} />
          <FlagTag value={r.aCroquis} label={t('liste.abreviations.croquis')} />
        </div>
      ),
    },
    {
      title: t('liste.colonnes.etat'), dataIndex: 'estComplet', key: 'etat', width: 110, align: 'center',
      export: (v) => v ? t('commun.complet') : t('commun.incomplet'),
      render: (v) => (
        <Tag
          color={v ? 'green' : 'orange'}
          style={{ borderRadius: 20, fontSize: 11, fontWeight: 600 }}
        >
          {v ? t('commun.complet') : t('commun.incomplet')}
        </Tag>
      ),
    },
    {
      title: t('liste.colonnes.sinistre'), dataIndex: 'numeroSinistreLocal', key: 'sin', width: 150,
      export: (v, r) => v || r?.referenceSinistreLiee || t('commun.nonAssocie'),
      render: (v, r) => v
        ? (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/sinistres/${r.sinistreTrackingId}`); }}
            style={{ background: 'none', border: 'none', color: GREEN, fontWeight: 600, cursor: 'pointer', padding: 0, fontSize: 12 }}
          >{v}</button>
        )
        : r.referenceSinistreLiee
          ? <span style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>{r.referenceSinistreLiee}</span>
          : <Tag color="default" style={{ borderRadius: 20, fontSize: 11 }}>{t('commun.nonAssocie')}</Tag>,
    },
  ];

  const title = modeNonAssocies ? t('liste.titreNonAssocies') : t('liste.titre');

  return (
    <Layout title={modeNonAssocies ? t('liste.nonAssocies') : t('liste.titreSinistres')}>
      {/* `pagination.total` est renseigne dans les deux modes : totalElements du
          backend en mode pagine, et la taille de la liste complete en mode
          « non-associes » (qui n'est pas pagine). C'est donc le seul compte valable
          des deux cotes - `filtered` ne voit que la page courante. */}
      <PageHeader
        titre={title}
        icone={modeNonAssocies ? <LinkOutlined /> : <FileTextOutlined />}
        compte={pagination.total}
        unite={t('liste.unite')}
          unitePluriel={t('liste.unitePluriel')}   /* invariable */
        actions={(
          <Space size={8}>
            <TableExportButton
              floating
              data={filtered}
              columns={columns}
              filename={modeNonAssocies ? 'pv-non-associes' : 'pv-sinistres'}
              title={modeNonAssocies ? t('liste.nonAssocies') : t('liste.titreSinistres')}
              subtitle={[
                filtreCompletude === 'true' && t('liste.filtres.completsUniquement'),
                filtreCompletude === 'false' && t('liste.filtres.incompletsUniquement'),
                search && t('liste.export.recherche', { search }),
              ].filter(Boolean).join(' · ') || t('liste.export.tous')}
            />
            {!modeNonAssocies && (
              <Tooltip title={t('liste.voirNonAssocies')}>
                <Button
                  icon={<LinkOutlined />}
                  onClick={() => navigate('/pv-sinistres/non-associes')}
                >
                  {t('liste.boutonNonAssocies')}
                </Button>
              </Tooltip>
            )}
            {modeNonAssocies && (
              <Button onClick={() => navigate('/pv-sinistres')}>{t('liste.tousLesPv')}</Button>
            )}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}
              onClick={() => setCreateOpen(true)}
            >
              {t('liste.enregistrer')}
            </Button>
          </Space>
        )}
      />

      <KpiCards items={kpis} columns={3} />

      {!modeNonAssocies && (
        <div className="cb-card" style={{ marginBottom: 20 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={10}>
              <Search
                placeholder={t('liste.filtres.recherche')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={6}>
              <Select
                placeholder={t('liste.filtres.etat')}
                style={{ width: '100%' }}
                value={filtreCompletude || undefined}
                allowClear
                onChange={(v) => setFiltreCompletude(v || '')}
              >
                <Option value="true">{t('liste.filtres.completsUniquement')}</Option>
                <Option value="false">{t('liste.filtres.incompletsUniquement')}</Option>
              </Select>
            </Col>
            <Col xs={24} md={6}>
              <RangePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={[t('liste.filtres.receptionDebut'), t('liste.filtres.receptionFin')]}
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                allowClear
              />
            </Col>
            <Col xs={24} md={2}>
              <Tooltip title={t('commun.actualiser')}>
                <Button icon={<ReloadOutlined />} onClick={() => { load(0); rafraichirStats(); }} />
              </Tooltip>
            </Col>
          </Row>
        </div>
      )}

      {modeNonAssocies && (
        <div className="cb-card" style={{ marginBottom: 20 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} md={22}>
              <Search
                placeholder={t('liste.filtres.rechercheNonAssocies')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} md={2}>
              <Tooltip title={t('commun.actualiser')}>
                <Button icon={<ReloadOutlined />} onClick={() => { load(0); rafraichirStats(); }} />
              </Tooltip>
            </Col>
          </Row>
        </div>
      )}

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={filtered}
          columns={columns}
          rowKey="pvTrackingId"
          loading={loading}
          scroll={{ x: 1200 }}
          onRow={(record) => ({
            onClick: () => setDetailPvId(record.pvTrackingId),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={modeNonAssocies ? t('liste.vide.nonAssocies') : t('liste.vide.aucun')}
              />
            ),
          }}
          pagination={modeNonAssocies ? false : {
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (total) => <span style={{ fontSize: 13, color: '#6b7280' }}>{t('liste.total', { count: total })}</span>,
            onChange: (p) => { setPagination(prev => ({ ...prev, current: p })); load(p - 1); },
          }}
        />
      </div>

      <NouveauPvModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { load(0); rafraichirStats(); }}
        defaultSinistreId={sinistreIdParam}
      />

      <DetailPvModal
        open={!!detailPvId}
        pvTrackingId={detailPvId}
        onClose={() => setDetailPvId(null)}
        onUpdated={() => { load(pagination.current - 1); rafraichirStats(); }}
      />
    </Layout>
  );
}
